"use client";

import { Button } from "@/components/rac/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/radix/dialog";
import { useLocationPermissionStore } from "@/stores/location-permission-store";
import { AlertCircle, MapPin, Navigation } from "lucide-react";
import { useEffect, useState } from "react";
interface LocationPermissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPermissionGranted?: (position: GeolocationPosition) => void;
  onPermissionDenied?: () => void;
  onCancel?: () => void;
}
export function LocationPermissionDialog({
  open,
  onOpenChange,
  onPermissionGranted,
  onPermissionDenied,
  onCancel
}: LocationPermissionDialogProps) {
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const [isGPSOffMode, setIsGPSOffMode] = useState(false);
  const {
    isRequestingPermission,
    isGettingLocation,
    errorMessage,
    requestLocationPermission,
    getCurrentLocation,
    clearError
  } = useLocationPermissionStore();

  // Clear error and reset GPS mode when dialog opens
  useEffect(() => {
    if (open) {
      clearError();
      setIsGPSOffMode(false);
      setLocalErrorMessage("");
    }
  }, [open, clearError]);
  const handleRequestPermission = async () => {
    try {
      const result = await requestLocationPermission();

      // Get updated state after permission request
      const {
        permissionStatus
      } = useLocationPermissionStore.getState();

      // Handle GPS off case specifically - show dedicated dialog
      if (result.isGPSOff) {
        setIsGPSOffMode(true);
        setLocalErrorMessage("Please turn on your device's Location/GPS switch.");
        // FIX: Don't call onPermissionDenied here - permission was granted, just GPS is off
        return;
      }
      if (permissionStatus === "granted" && !result.isGPSOff) {
        // Permission granted and GPS working, now get the actual location
        try {
          const position = await getCurrentLocation({
            enableHighAccuracy: true,
            timeout: 30000
          });
          onPermissionGranted?.(position);
          onOpenChange(false);
        } catch (locationError) {
          // FIX: If getting location fails after permission granted, could be GPS issue
          // Use duck typing for reliable error detection
          const isGeolocationError = locationError && typeof locationError === "object" && "code" in locationError && "message" in locationError;
          if (isGeolocationError) {
            const geoError = locationError as GeolocationPositionError;
            if (geoError.code === 2) {
              // POSITION_UNAVAILABLE
              setIsGPSOffMode(true);
              setLocalErrorMessage("Please turn on your device's Location/GPS switch.");
              // Don't call onPermissionDenied - this is GPS issue, not permission denial
            } else {
              setLocalErrorMessage("Failed to get your location. Please try again.");
              // FIX: Don't call onPermissionDenied for timeout/other errors either
            }
          } else {
            setLocalErrorMessage("Failed to get your location. Please try again.");
          }
        }
      } else if (permissionStatus === "denied") {
        // Permission denied - this is the only case where we should call onPermissionDenied
        setLocalErrorMessage("Location permission was denied. Please enable location access in your browser settings and try again.");
        onPermissionDenied?.();
      } else {
        // Other error
        setLocalErrorMessage("Failed to request location permission. Please try again.");
      }
    } catch (error) {
      setLocalErrorMessage("An unexpected error occurred while requesting location permission. Please try again.");
    }
  };
  const handleTryAgain = async () => {
    // When GPS is off, try getting location again to check if GPS was turned on
    try {
      const position_0 = await getCurrentLocation({
        enableHighAccuracy: true,
        timeout: 15000
      }); // Longer timeout since GPS was just enabled
      // Success! GPS is now on
      onPermissionGranted?.(position_0);
      onOpenChange(false);
    } catch (error_0) {
      // Still failing - use duck typing for reliable error detection
      const isGeolocationError_0 = error_0 && typeof error_0 === "object" && "code" in error_0 && "message" in error_0;
      if (isGeolocationError_0) {
        const geoError_0 = error_0 as GeolocationPositionError;
        if (geoError_0.code === 2) {
          // POSITION_UNAVAILABLE
          // GPS still off
          setIsGPSOffMode(true);
          setLocalErrorMessage("GPS is still off. Please enable location services on your device.");
        } else {
          // Other error
          setLocalErrorMessage("Failed to get your location. Please ensure GPS is enabled and try again.");
        }
      } else {
        // Non-geolocation error
        setLocalErrorMessage("Failed to get your location. Please ensure GPS is enabled and try again.");
      }
    }
  };
  const handleCancel = () => {
    // Only trigger onPermissionDenied for permission-related cancellations, not GPS off
    if (!isGPSOffMode) {
      onPermissionDenied?.();
    }
    onCancel?.();
    onOpenChange(false);
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<MapPin className="w-5 h-5 text-primary" />
						{isGPSOffMode ? "GPS Location Required" : "Location Permission Required"}
					</DialogTitle>
					<DialogDescription>
						{isGPSOffMode ? "Your device's GPS/location services appear to be turned off. Please enable location services on your device to continue." : "We need access to your location to show you nearby properties and provide better search results."}
					</DialogDescription>
				</DialogHeader>

				{(errorMessage || localErrorMessage) && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
						<div className="flex items-center gap-2 text-red-800">
							<AlertCircle className="w-4 h-4" />
							<span className="font-medium">Error:</span>
						</div>
						<p className="text-red-700 text-sm mt-1">{errorMessage || localErrorMessage}</p>
					</div>}

				<div className="flex flex-col gap-3 mt-6">
					<Button onClick={isGPSOffMode ? handleTryAgain : handleRequestPermission} isDisabled={isRequestingPermission || isGettingLocation} className="w-full">
						{isRequestingPermission || isGettingLocation ? <>
								<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
								Getting Location...
							</> : <>
								<Navigation className="w-4 h-4 mr-2" />
								{isGPSOffMode ? "Try Again" : "Allow Location Access"}
							</>}
					</Button>

					{!isGPSOffMode && <Button onClick={handleCancel} variant="outline" className="w-full" isDisabled={isRequestingPermission || isGettingLocation}>
							Cancel
						</Button>}
				</div>
			</DialogContent>
		</Dialog>;
}